import cronstrue from "cronstrue";
import type { AutomationRecord } from "@/lib/api/automations";
import { titleCaseSlug } from "@/lib/string-utils";

export type AutomationKind = "event" | "schedule" | "manual";
type Destination = "source" | "silent";
export type AutomationState = "enabled" | "error";

/** An owning agent is also valid on an external Automation; it is not its executor. */
export function automationExecutionMode(record?: {
	execution_config?: Record<string, unknown> | null;
	device_worker_id?: string | null;
	managed_agent_id?: string | null;
}): "external" | "script" | "device" | "agent" | "manual" {
	const executor = record?.execution_config?.executor;
	if (executor && typeof executor === "object" && "kind" in executor) {
		if (executor.kind === "external") return "external";
		if (executor.kind === "script") return "script";
	}
	if (record?.device_worker_id) return "device";
	return record?.managed_agent_id ? "agent" : "manual";
}

/** Only hosted/device agent execution has an Automation transcript. */
export function automationTranscriptAgent(
	record?: Parameters<typeof automationExecutionMode>[0],
): string | null {
	const mode = automationExecutionMode(record);
	return mode === "agent" || mode === "device"
		? (record?.managed_agent_id ?? null)
		: null;
}

export interface AutomationOutput {
	destination: Destination;
}

/** The identity row works with a bounded current-state projection too. Missing
 * optional metrics remain unknown, never manufactured run counts or statuses. */
export type AutomationIdentityRecord = Pick<
	AutomationRecord,
	"automation_id" | "name"
> &
	Partial<AutomationRecord>;

export interface Automation<
	TRecord extends AutomationIdentityRecord = AutomationRecord,
> {
	key: string;
	kind: AutomationKind;
	when: string;
	does: string;
	output: AutomationOutput;
	state: AutomationState;
	sortTime: number | null;
	automation: TRecord;
}

function describeCron(cron: string): string {
	try {
		return cronstrue.toString(cron.trim(), {
			throwExceptionOnParseError: true,
		});
	} catch {
		return `On schedule ${cron.trim()}`;
	}
}

/** Connector key → display name, from the connector catalog. */
type ConnectorNames = ReadonlyMap<string, string>;

/** What a trigger's connection says about its scope: its name and tenant. */
interface TriggerConnection {
	display_name: string | null;
	external_tenant_id: string | null;
}

/** Connection id → its name and tenant, for the connections triggers name. */
type TriggerConnections = ReadonlyMap<number, TriggerConnection>;

/**
 * The connector's own display name. Until the catalog names it, the key as
 * stored — never a name guessed by capitalising the key ("Gchat").
 */
function connectorLabel(key: string, names: ConnectorNames): string {
	return names.get(key) ?? key;
}

/**
 * Entity-type slugs are authored names, not connector keys, so they never go
 * through `connectorLabel`; a platform-owned slug would otherwise render its
 * raw `$` prefix (`$member`).
 */
function entityTypeLabel(slug: string): string {
	return titleCaseSlug(slug.replace(/^\$/, ""));
}

/**
 * A channel a connection is scoped to — its tenant IS the channel, as for a
 * claimed chat space — is named by that connection. Any other channel keeps
 * its id.
 */
function channelLabel(
	channelId: string,
	connection: TriggerConnection | undefined,
): string {
	const name = connection?.display_name?.trim();
	return name && connection?.external_tenant_id === channelId
		? name
		: `#${channelId}`;
}

function eventScope(
	match: unknown,
	connection: TriggerConnection | undefined,
): string {
	if (!match || typeof match !== "object" || Array.isArray(match)) return "";
	const fields = match as Record<string, unknown>;
	if (typeof fields.repository === "string") return ` in ${fields.repository}`;
	if (typeof fields.channel_id === "string")
		return ` in ${channelLabel(fields.channel_id, connection)}`;
	if (typeof fields.resource_ref === "string")
		return ` for ${fields.resource_ref}`;
	return "";
}

function asTriggerRecords(
	automation: AutomationIdentityRecord,
): Array<Record<string, unknown>> {
	if (!Array.isArray(automation.triggers)) return [];
	return automation.triggers.filter(
		(trigger): trigger is Record<string, unknown> =>
			Boolean(trigger) && typeof trigger === "object",
	);
}

/** Human-readable clause for one activation contract (no leading "When "). */
function describeTriggerClause(
	trigger: Record<string, unknown>,
	names: ConnectorNames,
	connections: TriggerConnections,
): string | null {
	if (trigger.kind === "schedule" && typeof trigger.cron === "string") {
		return describeCron(trigger.cron);
	}
	if (
		trigger.kind === "event" &&
		trigger.source !== "workspace" &&
		typeof trigger.connector_key === "string"
	) {
		const eventTypes = Array.isArray(trigger.event_types)
			? trigger.event_types.filter(
					(eventType): eventType is string => typeof eventType === "string",
				)
			: [];
		const eventLabel =
			eventTypes.length > 0 ? eventTypes.join(" or ") : "an event";
		const connection =
			typeof trigger.connection_id === "number"
				? connections.get(trigger.connection_id)
				: undefined;
		return `${connectorLabel(trigger.connector_key, names)} sends ${eventLabel}${eventScope(trigger.match, connection)}`;
	}
	if (trigger.kind === "event" && trigger.source === "workspace") {
		const eventTypes = Array.isArray(trigger.event_types)
			? trigger.event_types.filter(
					(eventType): eventType is string => typeof eventType === "string",
				)
			: [];
		const eventLabel =
			eventTypes.length > 0 ? eventTypes.join(" or ") : "an event";
		const entityScope =
			typeof trigger.entity_type === "string"
				? ` on ${entityTypeLabel(trigger.entity_type)}`
				: "";
		return `another Automation writes ${eventLabel}${entityScope}`;
	}
	return null;
}

function automationState(
	automation: AutomationIdentityRecord,
): AutomationState {
	return automation.health === "degraded" ? "error" : "enabled";
}

export function deriveAutomation<TRecord extends AutomationIdentityRecord>(
	automation: TRecord,
	connectorNames: ConnectorNames = new Map(),
	connections: TriggerConnections = new Map(),
): Automation<TRecord> {
	const triggers = asTriggerRecords(automation);
	const name = automation.name?.trim() || "Untitled automation";

	const hasEvent = triggers.some((trigger) => trigger.kind === "event");
	const hasSchedule = triggers.some((trigger) => trigger.kind === "schedule");
	const kind: AutomationKind = hasEvent
		? "event"
		: hasSchedule
			? "schedule"
			: "manual";

	const clauses = triggers
		.map((trigger) =>
			describeTriggerClause(trigger, connectorNames, connections),
		)
		.filter((clause): clause is string => Boolean(clause));
	let when = "When started manually";
	if (clauses.length === 1) {
		when = `When ${clauses[0]}`;
	} else if (clauses.length > 1) {
		when = `When ${clauses[0]}${clauses
			.slice(1)
			.map((clause) => `, or when ${clause}`)
			.join("")}`;
	}

	// Reply if any event trigger replies to source (multi-OR activations).
	const destination: Destination = triggers.some(
		(trigger) =>
			trigger.kind === "event" && trigger.output === "reply_to_source",
	)
		? "source"
		: "silent";

	return {
		key: `automation:${automation.automation_id}`,
		kind,
		when,
		does: name,
		output: { destination },
		state: automationState(automation),
		sortTime: automation.next_run_at
			? Date.parse(automation.next_run_at)
			: null,
		automation,
	};
}

const KIND_ORDER: Record<AutomationKind, number> = {
	event: 0,
	schedule: 1,
	manual: 2,
};

export function mergeAutomations(
	automations: AutomationRecord[],
	connectorNames: ConnectorNames = new Map(),
	connections: TriggerConnections = new Map(),
): Automation[] {
	return automations
		.map((automation) =>
			deriveAutomation(automation, connectorNames, connections),
		)
		.sort((a, b) => {
			if (a.state !== b.state) return a.state === "enabled" ? -1 : 1;
			if (KIND_ORDER[a.kind] !== KIND_ORDER[b.kind])
				return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
			if (a.sortTime !== b.sortTime) {
				if (a.sortTime === null) return 1;
				if (b.sortTime === null) return -1;
				return a.sortTime - b.sortTime;
			}
			return a.when.localeCompare(b.when) || a.key.localeCompare(b.key);
		});
}

export type KindFacet = "all" | AutomationKind;
export type OutputFacet = "any" | "reply" | "silent";
export type StateFacet = "any" | "active" | "failing";

export interface AutomationFacets {
	kind: KindFacet;
	output: OutputFacet;
	state: StateFacet;
}

export const DEFAULT_FACETS: AutomationFacets = {
	kind: "all",
	output: "any",
	state: "any",
};

export function filterAutomations(
	automations: Automation[],
	facets: AutomationFacets,
): Automation[] {
	return automations.filter(
		(automation) =>
			(facets.kind === "all" || automation.kind === facets.kind) &&
			(facets.output === "any" ||
				(facets.output === "reply"
					? automation.output.destination === "source"
					: automation.output.destination === "silent")) &&
			(facets.state === "any" ||
				(facets.state === "active"
					? automation.state === "enabled"
					: automation.state === "error")),
	);
}

/** The connector an automation subscribes to (its first event trigger), if any. */
export function eventConnectorKey(record: AutomationRecord): string | null {
	const triggers = Array.isArray(record.triggers) ? record.triggers : [];
	for (const trigger of triggers) {
		if (
			trigger &&
			typeof trigger === "object" &&
			(trigger as Record<string, unknown>).kind === "event" &&
			(trigger as Record<string, unknown>).source !== "workspace" &&
			typeof (trigger as Record<string, unknown>).connector_key === "string"
		) {
			return (trigger as Record<string, unknown>).connector_key as string;
		}
	}
	return null;
}
